import React, { useState } from 'react';
import { Download, Upload, AlertTriangle, CheckCircle, FileJson, RotateCcw, Eraser, Trash2 } from 'lucide-react';
import { colors } from '../../constants/theme';
import { backupService, competitionService } from '../../services/api';

const CONFIRM_WORD = 'RESTAURAR';
const NEW_CHAMPIONSHIP_WORD = 'NOVO CAMPEONATO';

export default function BackupManager({ onRestored }) {
  const [downloading, setDownloading] = useState(false);
  const [downloadMsg, setDownloadMsg] = useState(null);

  const [resetting, setResetting] = useState(false);
  const [resetMsg, setResetMsg] = useState(null);

  const [newChampConfirm, setNewChampConfirm] = useState('');
  const [startingNew, setStartingNew] = useState(false);
  const [newChampMsg, setNewChampMsg] = useState(null);

  const [file, setFile] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadMsg(null);
    try {
      const filename = await backupService.download();
      setDownloadMsg(`Backup salvo como ${filename}`);
    } catch (e) {
      setDownloadMsg(`Erro ao gerar backup: ${e.message || 'desconhecido'}`);
    } finally {
      setDownloading(false);
    }
  };

  const handleReset = async () => {
    const confirmed = window.confirm(
      'Reiniciar o campeonato?\n\n' +
        'Serão apagados: todas as partidas e placares, os grupos sorteados, ' +
        'a classificação e a fila.\n\n' +
        'Os inscritos e os atletas são mantidos. Não há como desfazer.',
    );
    if (!confirmed) return;

    setResetting(true);
    setResetMsg(null);
    try {
      const resp = await competitionService.reset();
      if (resp.success) {
        setResetMsg(resp.message || 'Campeonato reiniciado.');
        if (onRestored) await onRestored();
      }
    } catch (e) {
      setResetMsg(
        'Erro ao reiniciar: ' + (e.error?.message || e.message || 'desconhecido'),
      );
    } finally {
      setResetting(false);
    }
  };

  const canStartNew =
    !startingNew && newChampConfirm.trim().toUpperCase() === NEW_CHAMPIONSHIP_WORD;

  const handleStartNew = async () => {
    if (!canStartNew) return;

    setStartingNew(true);
    setNewChampMsg(null);
    try {
      const resp = await backupService.startNewChampionship();
      if (resp.success) {
        setNewChampMsg(resp.message || 'Novo campeonato iniciado.');
        setNewChampConfirm('');
        if (onRestored) await onRestored();
      }
    } catch (e) {
      setNewChampMsg(
        'Erro ao iniciar novo campeonato: ' + (e.error?.message || e.message || 'desconhecido'),
      );
    } finally {
      setStartingNew(false);
    }
  };

  const handleFileChange = async (e) => {
    const selected = e.target.files[0];
    setError(null);
    setResult(null);
    setConfirmText('');

    if (!selected) {
      setFile(null);
      setSnapshot(null);
      return;
    }

    try {
      const parsed = await backupService.parseFile(selected);
      setFile(selected);
      setSnapshot(parsed);
    } catch (err) {
      setFile(null);
      setSnapshot(null);
      setError(err.message);
    } finally {
      // Libera o input para que escolher o MESMO arquivo de novo dispare onChange
      e.target.value = '';
    }
  };

  const handleRestore = async () => {
    if (confirmText.trim().toUpperCase() !== CONFIRM_WORD) return;

    setRestoring(true);
    setError(null);
    try {
      const resp = await backupService.restore(snapshot);
      if (resp.success) {
        setResult(resp.data);
        setFile(null);
        setSnapshot(null);
        setConfirmText('');
        if (onRestored) await onRestored();
      }
    } catch (e) {
      setError(e.error?.message || e.message || 'Erro ao restaurar o backup');
    } finally {
      setRestoring(false);
    }
  };

  const canRestore =
    snapshot && !restoring && confirmText.trim().toUpperCase() === CONFIRM_WORD;

  const withProof = snapshot
    ? snapshot.registrations.filter((r) => r.paymentProofPath).length
    : 0;

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold" style={{ color: colors.primary }}>
        Backup e Restauração
      </h2>

      {/* ---------- Baixar ---------- */}
      <div className="bg-white rounded-lg shadow p-5">
        <div className="flex items-start gap-3 mb-3">
          <Download className="h-5 w-5 mt-0.5" style={{ color: colors.primary }} />
          <div>
            <h3 className="font-semibold">Baixar backup atual</h3>
            <p className="text-sm text-gray-600">
              Gera um arquivo .json com todas as inscrições e atletas de agora. Faça isso
              antes de restaurar qualquer coisa — é o seu ponto de retorno.
            </p>
            <p className="text-xs text-gray-500 mt-1">
              As imagens dos comprovantes não vão dentro do arquivo: elas ficam guardadas
              no servidor e nunca são apagadas, então o backup só precisa saber onde elas
              estão.
            </p>
          </div>
        </div>

        <button
          onClick={handleDownload}
          disabled={downloading}
          className="px-4 py-2 text-white rounded hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: colors.primary }}
        >
          {downloading ? 'Gerando...' : 'Baixar backup'}
        </button>
        {downloadMsg && (
          <p className="text-sm text-gray-600 mt-3">{downloadMsg}</p>
        )}
      </div>

      {/* ---------- Reiniciar campeonato ---------- */}
      <div className="bg-white rounded-lg shadow p-5 border-2 border-amber-200">
        <div className="flex items-start gap-3 mb-3">
          <Eraser className="h-5 w-5 mt-0.5 text-amber-600" />
          <div>
            <h3 className="font-semibold text-amber-700">
              Reiniciar campeonato (mantendo os inscritos)
            </h3>
            <p className="text-sm text-gray-600">
              Volta tudo ao ponto anterior ao sorteio, sem precisar de arquivo de
              backup. Útil para zerar depois dos testes.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3 mb-4 text-sm">
          <div className="p-3 bg-red-50 border border-red-200 rounded">
            <p className="font-medium text-red-700 mb-1">Apaga</p>
            <ul className="list-disc list-inside text-red-700 space-y-0.5">
              <li>todas as partidas e placares</li>
              <li>os grupos sorteados</li>
              <li>a classificação</li>
              <li>a fila de partidas</li>
            </ul>
          </div>
          <div className="p-3 bg-green-50 border border-green-200 rounded">
            <p className="font-medium text-green-800 mb-1">Mantém</p>
            <ul className="list-disc list-inside text-green-800 space-y-0.5">
              <li>todas as inscrições</li>
              <li>todos os atletas</li>
              <li>os comprovantes de pix</li>
            </ul>
          </div>
        </div>

        <button
          onClick={handleReset}
          disabled={resetting}
          className="px-4 py-2 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50"
        >
          {resetting ? 'Reiniciando...' : 'Reiniciar campeonato'}
        </button>
        {resetMsg && <p className="text-sm text-gray-600 mt-3">{resetMsg}</p>}
      </div>

      {/* ---------- Novo campeonato ---------- */}
      <div className="bg-white rounded-lg shadow p-5 border-2 border-red-300">
        <div className="flex items-start gap-3 mb-3">
          <Trash2 className="h-5 w-5 mt-0.5 text-red-600" />
          <div>
            <h3 className="font-semibold text-red-700">Começar um campeonato novo (apaga tudo)</h3>
            <p className="text-sm text-gray-600">
              Deixa o sistema vazio para abrir as inscrições de um novo campeonato.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3 mb-4 text-sm">
          <div className="p-3 bg-red-50 border border-red-200 rounded">
            <p className="font-medium text-red-700 mb-1">Apaga</p>
            <ul className="list-disc list-inside text-red-700 space-y-0.5">
              <li>todas as inscrições</li>
              <li>todos os atletas</li>
              <li>partidas, placares, grupos e classificação</li>
              <li>a fila de partidas</li>
            </ul>
          </div>
          <div className="p-3 bg-green-50 border border-green-200 rounded">
            <p className="font-medium text-green-800 mb-1">Mantém</p>
            <ul className="list-disc list-inside text-green-800 space-y-0.5">
              <li>o login do organizador</li>
              <li>as imagens dos comprovantes no servidor</li>
            </ul>
          </div>
        </div>

        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded mb-4">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-amber-800">
            Baixe o backup acima antes — ele é a única forma de recuperar o campeonato
            anterior. Não há como desfazer.
          </p>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Para confirmar, digite <strong>{NEW_CHAMPIONSHIP_WORD}</strong>
          </label>
          <input
            type="text"
            value={newChampConfirm}
            onChange={(e) => setNewChampConfirm(e.target.value)}
            placeholder={NEW_CHAMPIONSHIP_WORD}
            className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded"
          />
        </div>

        <button
          onClick={handleStartNew}
          disabled={!canStartNew}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {startingNew ? 'Apagando...' : 'Apagar tudo e começar novo campeonato'}
        </button>
        {newChampMsg && <p className="text-sm text-gray-600 mt-3">{newChampMsg}</p>}
      </div>

      {/* ---------- Restaurar ---------- */}
      <div className="bg-white rounded-lg shadow p-5 border-2 border-red-200">
        <div className="flex items-start gap-3 mb-4">
          <RotateCcw className="h-5 w-5 mt-0.5 text-red-600" />
          <div>
            <h3 className="font-semibold text-red-700">
              Reiniciar campeonato a partir de um backup
            </h3>
            <p className="text-sm text-gray-600">
              Restaura as inscrições e os atletas exatamente como estavam no arquivo.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded mb-4">
          <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-red-700">
            <p className="font-medium mb-1">Esta ação apaga o andamento do campeonato:</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>todas as partidas, incluindo placares já registrados</li>
              <li>os grupos sorteados e a classificação</li>
              <li>a fila de partidas</li>
              <li>as inscrições e atletas atuais, substituídos pelos do arquivo</li>
            </ul>
            <p className="mt-2">Não há como desfazer.</p>
            <p className="mt-1">
              Os comprovantes de pix no servidor não são tocados — nenhum arquivo é
              apagado.
            </p>
          </div>
        </div>

        {result && (
          <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded mb-4">
            <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-green-800">
              <p className="font-medium">Campeonato reiniciado.</p>
              <p>
                {result.playersRestored} atleta(s) e {result.registrationsRestored}{' '}
                inscrição(ões) restaurados.
              </p>
              {result.discarded && (
                <p className="text-green-700 mt-1">
                  Descartados: {result.discarded.matches} partida(s) e{' '}
                  {result.discarded.competitions} competição(ões).
                </p>
              )}
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded mb-4">
            <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 mb-4">
          {file ? (
            <>
              <FileJson className="w-6 h-6 text-green-500 mb-1" />
              <p className="text-sm text-gray-700">{file.name}</p>
              <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(0)} KB</p>
            </>
          ) : (
            <>
              <Upload className="w-6 h-6 text-gray-500 mb-1" />
              <p className="text-sm text-gray-700">Clique para escolher o arquivo de backup</p>
              <p className="text-xs text-gray-500">.json exportado por esta tela</p>
            </>
          )}
          <input
            type="file"
            accept="application/json,.json"
            onChange={handleFileChange}
            className="hidden"
          />
        </label>

        {snapshot && (
          <>
            <div className="p-3 bg-gray-50 border rounded mb-4 text-sm">
              <p className="font-medium mb-2">O arquivo contém:</p>
              <div className="grid grid-cols-2 gap-2 text-gray-700">
                <p>{snapshot.players.length} atleta(s)</p>
                <p>{snapshot.registrations.length} inscrição(ões)</p>
              </div>
              <p className="text-gray-700 mt-2">
                {withProof} inscrição(ões) com comprovante de pix
              </p>
              {snapshot.exportedAt && (
                <p className="text-xs text-gray-500 mt-2">
                  Exportado em{' '}
                  {new Date(snapshot.exportedAt).toLocaleString('pt-BR')}
                </p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Para confirmar, digite <strong>{CONFIRM_WORD}</strong>
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={CONFIRM_WORD}
                className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded"
              />
            </div>
          </>
        )}

        <button
          onClick={handleRestore}
          disabled={!canRestore}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {restoring ? 'Restaurando...' : 'Reiniciar campeonato com este backup'}
        </button>
      </div>
    </div>
  );
}
